import { Injectable } from '@angular/core';

import { MatchStatus } from '../../../models/common/match-status.enum';
import {
  LiveScoreMatchPayload,
  LiveScoreRow,
  SubMatchPayload,
} from '../../../models/common/live-score-match.model';
import { sortMatchesByTableThenStartTimeInPlace, resolveTableIdsForTieRow } from '../../../utilities/utils/match-sort.util';
import { RowRemovalAnimator } from '../../../utilities/utils/row-removal-animator.util';

/** Host-provided callbacks and flags for list reconciliation. */
export interface LiveScorecardsListServiceConfig {
  /** Invoked after list or overlay state changes so the host can run CD. */
  onStateChange: () => void;
  /** Whether sub-license filtering is active. */
  isSubLicense: () => boolean;
  /** Current user role id used for sub-license filtering. */
  userRoleId: () => number;
}

/**
 * Owns live scorecard list state and reconciliation: REST row expansion,
 * websocket upserts/removals, tie sub-match fan-out, and removal animation
 * bookkeeping.
 */
@Injectable()
export class LiveScorecardsListService {
  liveScoredMatchesList: LiveScoreRow[] = [];

  private readonly rowRemovalAnimator = new RowRemovalAnimator();

  /** Row ids currently showing the completed removal overlay. */
  get removingRowIds(): Set<string> {
    return this.rowRemovalAnimator.removingRowIds;
  }

  private onStateChange: (() => void) | null = null;
  private isSubLicense: (() => boolean) | null = null;
  private userRoleId: (() => number) | null = null;

  /**
   * Wires host callbacks once when the scorecards list component initialises.
   *
   * @param config - Change-detection and sub-license hooks from the host.
   */
  configure(config: LiveScorecardsListServiceConfig): void {
    this.onStateChange = config.onStateChange;
    this.isSubLicense = config.isSubLicense;
    this.userRoleId = config.userRoleId;
  }

  /** Clears pending removal timers and overlay state on host destroy. */
  destroy(): void {
    this.rowRemovalAnimator.destroy();
  }

  /** Resets the in-memory list — used when search/filter changes. */
  resetList(): void {
    this.liveScoredMatchesList = [];
    this.notifyStateChange();
  }

  /**
   * Merges one REST page into the list, expanding team ties and deduping rows
   * on pagination load-more.
   *
   * @param rawData - Server page payload before row expansion.
   * @param isLoadMore - Whether this page appends to an existing list.
   */
  mergeRestPage(rawData: LiveScoreMatchPayload[], isLoadMore: boolean): void {
    let newData = rawData.flatMap((match) => this.buildLiveScoreEntries(match));

    if (isLoadMore) {
      const existingRowIds = new Set(
        this.liveScoredMatchesList.map((row) => row.__row_id),
      );
      newData = newData.filter((row) => !existingRowIds.has(row.__row_id));
    }

    this.liveScoredMatchesList = isLoadMore
      ? [...this.liveScoredMatchesList, ...newData]
      : newData;

    sortMatchesByTableThenStartTimeInPlace(this.liveScoredMatchesList);
    this.notifyStateChange();
  }

  /**
   * Reconciles one websocket batch against the current list.
   *
   * @param message - Single update or batch from the socket envelope.
   */
  updateMatchData(
    message: LiveScoreMatchPayload | LiveScoreMatchPayload[],
  ): void {
    const incomingMessages = Array.isArray(message) ? message : [message];

    incomingMessages.forEach((msg) => {
      if (msg.type === 2) {
        this.applyTeamMatchUpdate(msg);
        return;
      }

      const rowId = String(msg.id);
      const matchIndex = this.liveScoredMatchesList.findIndex(
        (row) => row.id === msg.id,
      );

      if (!this.isConfirmedFinish(rowId, msg.status, msg.live_score)) {
        if (matchIndex !== -1) {
          this.removeRowWithAnimation(
            this.liveScoredMatchesList[matchIndex].__row_id,
          );
        }
        return;
      }

      this.rowRemovalAnimator.cancel(rowId);
      const row = this.toRow(msg, rowId);
      if (matchIndex === -1) {
        this.insertNewMatch([row], msg);
      } else {
        this.liveScoredMatchesList[matchIndex] = row;
      }
    });

    sortMatchesByTableThenStartTimeInPlace(this.liveScoredMatchesList);
    this.notifyStateChange();
  }

  /**
   * Expands one match into the list row(s) it should be rendered as. Used for
   * REST page loads only — applyTeamMatchUpdate has its own non-stateful
   * fallback for socket batches, to avoid double-evaluating isRenderableSubMatch.
   *
   * @param match - Raw REST page match payload.
   * @returns Zero or more display rows for the match.
   */
  buildLiveScoreEntries(match: LiveScoreMatchPayload): LiveScoreRow[] {
    if (match.status === MatchStatus.SCORED) {
      return [];
    }

    if (
      match.type !== 2 ||
      !Array.isArray(match.sub_matches) ||
      match.sub_matches.length === 0
    ) {
      return [this.toRow(match, String(match.id))];
    }

    const hasStarted = match.sub_matches.some(
      (sub) => sub.status !== MatchStatus.NOT_STARTED,
    );
    if (!hasStarted) {
      return [this.toRow(match, String(match.id))];
    }

    const renderableSubMatches = match.sub_matches.filter((sub) =>
      this.isRenderableSubMatch(sub, `${match.id}_${sub.id}`),
    );

    return renderableSubMatches.map((sub) => this.buildTieSubMatchRow(match, sub));
  }

  /**
   * Builds one list row for a type-2 tie board with per-board state and table.
   *
   * @param parent - Tie-level match payload.
   * @param sub - Sub-match this row represents.
   * @returns Row ready for template binding.
   */
  private buildTieSubMatchRow(
    parent: LiveScoreMatchPayload,
    sub: SubMatchPayload,
  ): LiveScoreRow {
    const rowId = `${parent.id}_${sub.id}`;
    return this.withResolvedTableIds(
      this.toRow(
        {
          ...parent,
          status: sub.status,
          live_score: sub.live_score,
          live_score_json: sub.live_score_json,
        },
        rowId,
      ),
      sub,
    );
  }

  /**
   * Resolves `table_ids` per type-2 board for table-wise sorting and display.
   *
   * @param row - Row about to enter the list.
   * @param sub - Sub-match this row represents, when known.
   * @returns Row with resolved `table_ids` when applicable.
   */
  private withResolvedTableIds(
    row: LiveScoreRow,
    sub?: SubMatchPayload,
  ): LiveScoreRow {
    if (row.type === 2) {
      return {
        ...row,
        table_ids: resolveTableIdsForTieRow(row, sub),
      };
    }
    return row;
  }

  /**
   * Live-only feed — `SCORED` with `live_score === false` is never shown.
   *
   * @param _rowId - Stable row identity (`__row_id`).
   * @param status - Lifecycle status of the board/match.
   * @param liveScore - Whether the board/match is still actively live-scored.
   * @returns True when the row should be shown now.
   */
  private isConfirmedFinish(
    _rowId: string,
    status: MatchStatus,
    liveScore: boolean | undefined,
  ): boolean {
    if (status === MatchStatus.SCORED && liveScore === false) {
      return false;
    }
    return true;
  }

  /**
   * A sub-match belongs on the board once it has started (live or scored),
   * going through isConfirmedFinish so an unconfirmed finish is hidden once
   * rather than shown immediately.
   *
   * @param sub - Sub-match payload (status, live_score, live_score_json).
   * @param rowId - Stable row identity (`__row_id`) for this board.
   * @returns True when the board should be rendered as its own row.
   */
  private isRenderableSubMatch(sub: SubMatchPayload, rowId: string): boolean {
    if (sub.status !== MatchStatus.IN_PROGRESS && sub.status !== MatchStatus.SCORED) {
      return false;
    }
    return this.isConfirmedFinish(rowId, sub.status, sub.live_score);
  }

  /**
   * Reconciles a type-2 team tie socket update against existing rows. A board
   * that finishes (SCORED) is updated in place and stays visible once
   * confirmed; only a board that reverts to NOT_STARTED, or is an unconfirmed
   * finish (see isConfirmedFinish), is removed.
   *
   * @param msg - Team tie payload, optionally carrying changed sub-matches only.
   */
  private applyTeamMatchUpdate(msg: LiveScoreMatchPayload): void {
    const subMatches = Array.isArray(msg.sub_matches) ? msg.sub_matches : [];

    if (subMatches.length === 0) {
      const rowId = String(msg.id);
      const existingIndex = this.liveScoredMatchesList.findIndex(
        (row) => row.id === msg.id,
      );

      if (!this.isConfirmedFinish(rowId, msg.status, msg.live_score)) {
        if (existingIndex !== -1) {
          this.removeRowWithAnimation(
            this.liveScoredMatchesList[existingIndex].__row_id,
          );
        }
        return;
      }

      this.rowRemovalAnimator.cancel(rowId);
      const row = this.toRow(msg, rowId);
      if (existingIndex === -1) {
        this.insertNewMatch([row], msg);
      } else {
        this.liveScoredMatchesList[existingIndex] = row;
      }
      return;
    }

    // Resolved before the loop below mutates the list, and reapplied after it.
    const mergedSubs = this.mergeKnownSubMatches(msg.id, subMatches);
    const tieMsg: LiveScoreMatchPayload = { ...msg, sub_matches: mergedSubs };

    subMatches.forEach((sub) => {
      const rowId = `${msg.id}_${sub.id}`;
      const existingIndex = this.liveScoredMatchesList.findIndex(
        (row) => row.__row_id === rowId,
      );

      if (this.isRenderableSubMatch(sub, rowId)) {
        this.rowRemovalAnimator.cancel(rowId);
        const row = this.buildTieSubMatchRow(tieMsg, sub);
        if (existingIndex === -1) {
          this.insertNewMatch([row], msg);
        } else {
          this.liveScoredMatchesList[existingIndex] = row;
        }
      } else if (existingIndex >= 0) {
        this.removeRowWithAnimation(rowId);
      }
    });

    this.applyMergedSubMatches(msg.id, mergedSubs);

    const hasAnyRowForTie = this.liveScoredMatchesList.some(
      (row) =>
        row.id === msg.id || String(row.__row_id).startsWith(`${msg.id}_`),
    );

    // Only fall back to a bare tie-level row when every board in this message
    // is genuinely NOT_STARTED.
    const allSubsNotStarted = subMatches.every(
      (sub) => sub.status === MatchStatus.NOT_STARTED,
    );
    if (!hasAnyRowForTie && allSubsNotStarted && msg.status !== MatchStatus.SCORED) {
      this.insertNewMatch([this.toRow(msg, String(msg.id))], msg);
    }
  }

  /**
   * Prepends rows when a match is newly live and passes sub-license checks.
   *
   * @param entries - Rows to insert at the front of the list.
   * @param msg - Source message used for sub-license filtering.
   */
  private insertNewMatch(
    entries: LiveScoreRow[],
    msg: LiveScoreMatchPayload,
  ): void {
    if (
      this.isSubLicense?.() &&
      msg.event_details?.user_role_id !== this.userRoleId?.()
    ) {
      return;
    }
    this.liveScoredMatchesList.unshift(...entries);
  }

  /**
   * Shows the completed overlay briefly, then removes the row from the list.
   *
   * @param rowId - Stable row identity (`__row_id`).
   */
  private removeRowWithAnimation(rowId: string): void {
    this.rowRemovalAnimator.remove(rowId, () => {
      this.liveScoredMatchesList = this.liveScoredMatchesList.filter(
        (row) => row.__row_id !== rowId,
      );
      this.notifyStateChange();
    });
    this.notifyStateChange();
  }

  /**
   * Attaches a stable list identity to a match payload.
   *
   * @param match - Source match fields.
   * @param rowId - Dedupe/trackBy id for this row.
   * @returns Row ready for template binding.
   */
  private toRow(match: LiveScoreMatchPayload, rowId: string): LiveScoreRow {
    return { ...match, __row_id: rowId };
  }

  /**
   * Puts every row for a tie on the same board list. Only the ticked board's
   * row is rebuilt during reconciliation, but each row renders the whole list
   * (progress dots + "Sub-matches" dialog), so the untouched rows would
   * otherwise keep a stale copy and show stale scores for the other boards.
   *
   * @param tieId - Parent tie id whose rows should be updated.
   * @param subMatches - Merged board list to apply to all of them.
   */
  private applyMergedSubMatches(
    tieId: number,
    subMatches: SubMatchPayload[],
  ): void {
    this.liveScoredMatchesList = this.liveScoredMatchesList.map((row) =>
      row.id === tieId ? { ...row, sub_matches: subMatches } : row,
    );
  }

  /**
   * Unions a tie's incoming boards with every board already known from the
   * rows currently rendered for that tie, so no row ever carries a partial
   * `sub_matches` list. A socket tick sends only the boards that changed, but
   * `toRow` spreads the whole tie payload onto every row, so using the message
   * as-is would shrink a 10-board tie down to whatever it touched. Incoming
   * fields win for a board present in both.
   *
   * @param tieId - Parent tie id whose rows should be scanned.
   * @param incoming - Boards carried by this message (may be a subset).
   * @returns The full board list for the tie, incoming values applied.
   */
  private mergeKnownSubMatches(
    tieId: number,
    incoming: SubMatchPayload[],
  ): SubMatchPayload[] {
    const merged = new Map<number, SubMatchPayload>();
    this.liveScoredMatchesList.forEach((row) => {
      if (row.id === tieId) {
        (row.sub_matches ?? []).forEach((sub) => merged.set(sub.id, sub));
      }
    });
    incoming.forEach((sub) =>
      merged.set(sub.id, { ...merged.get(sub.id), ...sub }),
    );
    return [...merged.values()];
  }

  /** Notifies the host to run change detection when state mutates. */
  private notifyStateChange(): void {
    this.onStateChange?.();
  }
}
